// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Users, AlertTriangle, CheckCircle, BarChart3 } from "lucide-react";
import { useStaffRoles, useEventStaffNeeds, useStaffAssignments, useUpcomingEventsForStaffing, useVenues } from "@/hooks/useStaffingData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const EventStaffNeedsTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEventsForStaffing();
  const { data: roles } = useStaffRoles();
  const queryClient = useQueryClient();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { data: needs, isLoading: needsLoading } = useEventStaffNeeds(selectedEventId);
  const { data: assignments } = useStaffAssignments(selectedEventId);

  const [addOpen, setAddOpen] = useState(false);
  const [addVenueId, setAddVenueId] = useState("");
  const [addForm, setAddForm] = useState({ role_id: "", quantity_needed: "1", shift_date: "", shift_start: "", shift_end: "", notes: "" });

  const selectedEvent = events?.find((e: any) => e.id === selectedEventId);

  // Collect venue IDs for the selected event
  const venueIds = useMemo(() => {
    if (!selectedEvent) return [];
    const ids: string[] = [];
    if (selectedEvent.venue_id) ids.push(selectedEvent.venue_id);
    if (selectedEvent.additional_venue_ids && Array.isArray(selectedEvent.additional_venue_ids)) {
      selectedEvent.additional_venue_ids.forEach((vid: string) => {
        if (!ids.includes(vid)) ids.push(vid);
      });
    }
    return ids;
  }, [selectedEvent]);

  const { data: venueData } = useVenues(venueIds);

  const getVenueName = (venueId: string) => {
    const v = venueData?.find((venue: any) => venue.id === venueId);
    if (v) return `${v.name} — ${v.city}${v.state ? `, ${v.state}` : ""}`;
    if (selectedEvent?.venue_id === venueId && selectedEvent?.venue_name) return `${selectedEvent.venue_name} — ${selectedEvent.venue_city || ""}`;
    return venueId;
  };

  // Count assignments per venue+role
  const getAssignedCount = (venueId: string, roleId: string) => {
    return (assignments || []).filter((a: any) => a.venue_id === venueId && a.role_id === roleId && a.status !== "cancelled").length;
  };

  // Group needs by venue
  const needsByVenue = useMemo(() => {
    const map: Record<string, any[]> = {};
    venueIds.forEach(vid => { map[vid] = []; });
    (needs || []).forEach((n: any) => {
      if (!map[n.venue_id]) map[n.venue_id] = [];
      map[n.venue_id].push(n);
    });
    return map;
  }, [needs, venueIds]);

  // Totals
  const totalNeeded = (needs || []).reduce((sum: number, n: any) => sum + (n.quantity_needed || 0), 0);
  const totalAssigned = (needs || []).reduce((sum: number, n: any) => sum + getAssignedCount(n.venue_id, n.role_id), 0);
  const totalGaps = totalNeeded - totalAssigned;
  const pctStaffed = totalNeeded > 0 ? Math.round((totalAssigned / totalNeeded) * 100) : 0;

  const handleAddNeed = async () => {
    if (!addForm.role_id || !addVenueId || !selectedEventId) { toast.error("Role and venue are required"); return; }
    const { error } = await supabase.from("manage_event_staff_needs").insert({
      event_id: selectedEventId,
      venue_id: addVenueId,
      role_id: addForm.role_id,
      quantity_needed: parseInt(addForm.quantity_needed) || 1,
      shift_date: addForm.shift_date || null,
      shift_start: addForm.shift_start || null,
      shift_end: addForm.shift_end || null,
      notes: addForm.notes || null,
    });
    if (error) { toast.error("Failed to add need"); return; }
    toast.success("Staff need added");
    setAddOpen(false);
    setAddForm({ role_id: "", quantity_needed: "1", shift_date: "", shift_start: "", shift_end: "", notes: "" });
    queryClient.invalidateQueries({ queryKey: ["event-staff-needs", selectedEventId] });
  };

  const handleUpdateQty = async (needId: string, qty: number) => {
    const { error } = await supabase.from("manage_event_staff_needs").update({ quantity_needed: qty }).eq("id", needId);
    if (error) { toast.error("Failed to update"); return; }
    queryClient.invalidateQueries({ queryKey: ["event-staff-needs", selectedEventId] });
  };

  const handleDeleteNeed = async (needId: string) => {
    const { error } = await supabase.from("manage_event_staff_needs").delete().eq("id", needId);
    if (error) { toast.error("Failed to delete"); return; }
    toast.success("Removed");
    queryClient.invalidateQueries({ queryKey: ["event-staff-needs", selectedEventId] });
  };

  if (evLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="min-w-[280px]">
          <Label>Select Event</Label>
          <Select value={selectedEventId || ""} onValueChange={(v) => setSelectedEventId(v)}>
            <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
            <SelectContent>
              {(events || []).map((e: any) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name} ({format(parseLocalDate(e.start_date), "MMM d")} - {format(parseLocalDate(e.end_date), "MMM d, yyyy")})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedEventId && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-4 flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              <div><p className="text-2xl font-bold">{totalNeeded}</p><p className="text-sm text-muted-foreground">Total Needed</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div><p className="text-2xl font-bold">{totalAssigned}</p><p className="text-sm text-muted-foreground">Assigned</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className={`h-8 w-8 ${totalGaps > 0 ? "text-destructive" : "text-green-600"}`} />
              <div><p className="text-2xl font-bold">{totalGaps > 0 ? totalGaps : 0}</p><p className="text-sm text-muted-foreground">Gaps</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-primary" />
              <div><p className="text-2xl font-bold">{pctStaffed}%</p><p className="text-sm text-muted-foreground">Staffed</p></div>
            </CardContent></Card>
          </div>

          {needsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            venueIds.map(venueId => (
              <Card key={venueId}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{getVenueName(venueId)}</span>
                    <Dialog open={addOpen && addVenueId === venueId} onOpenChange={(o) => { setAddOpen(o); if (o) setAddVenueId(venueId); }}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" onClick={() => setAddVenueId(venueId)}><Plus className="h-4 w-4 mr-1" />Add Need</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Add Staff Need — {getVenueName(venueId)}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                          <div><Label>Role *</Label>
                            <Select value={addForm.role_id} onValueChange={(v) => setAddForm({ ...addForm, role_id: v })}>
                              <SelectTrigger><SelectValue placeholder="Select role..." /></SelectTrigger>
                              <SelectContent>{(roles || []).filter((r: any) => r.is_active !== false).map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div><Label>Quantity Needed</Label><Input type="number" min={1} value={addForm.quantity_needed} onChange={(e) => setAddForm({ ...addForm, quantity_needed: e.target.value })} /></div>
                          <div><Label>Shift Date</Label><Input type="date" value={addForm.shift_date} onChange={(e) => setAddForm({ ...addForm, shift_date: e.target.value })} /></div>
                          <div className="grid grid-cols-2 gap-2">
                            <div><Label>Shift Start</Label><Input type="time" value={addForm.shift_start} onChange={(e) => setAddForm({ ...addForm, shift_start: e.target.value })} /></div>
                            <div><Label>Shift End</Label><Input type="time" value={addForm.shift_end} onChange={(e) => setAddForm({ ...addForm, shift_end: e.target.value })} /></div>
                          </div>
                          <div><Label>Notes</Label><Input value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} /></div>
                          <Button onClick={handleAddNeed} className="w-full">Add Need</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(needsByVenue[venueId] || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">No staff needs defined for this venue yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Role</TableHead>
                            <TableHead className="text-center">Needed</TableHead>
                            <TableHead className="text-center">Assigned</TableHead>
                            <TableHead className="text-center">Gap</TableHead>
                            <TableHead>Shift Date</TableHead>
                            <TableHead>Shift Times</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(needsByVenue[venueId] || []).map((need: any) => {
                            const assigned = getAssignedCount(venueId, need.role_id);
                            const gap = need.quantity_needed - assigned;
                            return (
                              <TableRow key={need.id}>
                                <TableCell className="font-medium">{need.role?.name || "—"}</TableCell>
                                <TableCell className="text-center">
                                  <Input
                                    type="number"
                                    min={0}
                                    className="w-16 mx-auto text-center"
                                    value={need.quantity_needed}
                                    onChange={(e) => handleUpdateQty(need.id, parseInt(e.target.value) || 0)}
                                  />
                                </TableCell>
                                <TableCell className="text-center font-semibold">{assigned}</TableCell>
                                <TableCell className="text-center">
                                  {gap > 0 ? (
                                    <Badge variant="destructive">{gap} needed</Badge>
                                  ) : (
                                    <Badge variant="success"><CheckCircle className="h-3 w-3 mr-1" />Filled</Badge>
                                  )}
                                </TableCell>
                                <TableCell>{need.shift_date ? format(new Date(need.shift_date), "MMM d, yyyy") : "—"}</TableCell>
                                <TableCell>{need.shift_start && need.shift_end ? `${need.shift_start} – ${need.shift_end}` : "—"}</TableCell>
                                <TableCell className="text-sm text-muted-foreground max-w-[120px] truncate">{need.notes || "—"}</TableCell>
                                <TableCell className="text-right">
                                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteNeed(need.id)}>
                                    <span className="sr-only">Delete</span>×
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </>
      )}

      {!selectedEventId && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Select an event to plan staffing needs.</CardContent></Card>
      )}
    </div>
  );
};

export default EventStaffNeedsTab;
